import React from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { SKYNET_INTERNAL_COMPANY_ID } from 'app/auth/authRoles';
import { COMPANY_TABS_ROUTING_PARAMS } from 'app/configs/routesConfig/companyTabs';

import { PageCodeComparisonType } from 'constants/claims';
import { ANY_HEADER_VIEW_PAGE_CODE } from 'constants/headerViewPermissions';
import { PAGE_CODES } from 'constants/pageCodes';
import { lazyWithRetry } from 'utils/chunkLoadRecovery';
import { EnhancedPath } from 'utils/history';

const AiChatDefaultsRedirect = () => (
  <Redirect to={`/company/${SKYNET_INTERNAL_COMPANY_ID}/${COMPANY_TABS_ROUTING_PARAMS.aiModels}`} />
);

const GuidelinesImportRedirect = () => <Redirect to={SystemPaths.guidelines.path} />;

const buildGuidelineDeepLink = ({ criteriaId, versionId, sectionId }) => {
  const params = new URLSearchParams();
  params.set('tab', 'sections');

  if (versionId) {
    params.set('versionId', versionId);
  }

  if (sectionId) {
    params.set('sectionId', sectionId);
  }

  const search = params.toString();
  const basePath = SystemPaths.guideline.generatePath({
    [SYSTEM_ROUTING_PARAMS.criteriaId]: criteriaId,
  });

  return `${basePath}?${search}`;
};

const GuidelineVersionRedirect = () => {
  const params = useParams();

  return <Redirect to={buildGuidelineDeepLink(params)} />;
};

export const SYSTEM_ROUTING_PARAMS = {
  roleId: 'roleId',
  tabName: 'tabName',
  emailTemplateId: 'emailTemplateId',
  emailTemplateCategoryId: 'emailTemplateCategoryId',
  documentTypeId: 'documentTypeId',
  documentFieldTypeId: 'documentFieldTypeId',
  taskCategoryId: 'taskCategoryId',
  criteriaId: 'criteriaId',
  taskTypeId: 'taskTypeId',
  actionType: 'actionType',
  loanHeaderViewId: 'loanHeaderViewId',
  loanEditorCustomFormId: 'loanEditorCustomFormId',
  feeProviderId: 'feeProviderId',
  feeProviderCategoryId: 'feeProviderCategoryId',
  loanActionId: 'loanActionId',
  checklistEntityId: 'checklistEntityId',
  ruleId: 'ruleId',
  pipelineId: 'pipelineId',
  versionId: 'versionId',
  sectionId: 'sectionId',
  groupKey: 'groupKey',
  customFieldDefinitionsTab: 'customFieldDefinitionsTab',
  packageId: 'packageId',
  methodId: 'methodId',
  campaignId: 'campaignId',
  providerKey: 'providerKey',
};

export class SystemPaths {
  static system = new EnhancedPath('/system');

  static categoryManager = new EnhancedPath('/category-manager');

  static contactTypes = new EnhancedPath('/contact-types');

  static contactTypesTab = new EnhancedPath(`${SystemPaths.contactTypes.path}/:tab?`);

  static documentTypes = new EnhancedPath('/document-types');

  static documentFieldTypes = new EnhancedPath('/document-field-types');

  static documentFieldType = new EnhancedPath(
    `${SystemPaths.documentFieldTypes.path}/:${SYSTEM_ROUTING_PARAMS.documentFieldTypeId}?`,
  );

  static documentBundles = new EnhancedPath('/document-bundles');

  static documentExtractionFieldsMapping = new EnhancedPath('/document-extraction-fields-mapping');

  static analytics = new EnhancedPath('/analytics');

  static activeApplicants = new EnhancedPath('/active-applicants');

  static roles = new EnhancedPath('/security-groups');

  static loanRoles = new EnhancedPath('/loan-roles');

  static dripCampaigns = new EnhancedPath('/drip-campaigns');
  static dripCampaignBuilder = new EnhancedPath(
    `${SystemPaths.dripCampaigns.path}/:${SYSTEM_ROUTING_PARAMS.campaignId}`,
  );

  static aiProcesses = new EnhancedPath('/ai-processes');
  static aiProcessNew = new EnhancedPath(`${SystemPaths.aiProcesses.path}/new`);
  static aiProcess = new EnhancedPath(
    `${SystemPaths.aiProcesses.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}/:${SYSTEM_ROUTING_PARAMS.tabName}?`,
  );

  // LEN-213 (designer D3): the rule libraries, the rule editor and the test on stored runs.
  static aiRuleLibraries = new EnhancedPath('/ai-rule-libraries');

  static knowledgeItems = new EnhancedPath('/knowledge-items');

  static aiLog = new EnhancedPath('/ai-log');

  static aiUsage = new EnhancedPath(`/ai-usage/:${SYSTEM_ROUTING_PARAMS.tabName}?`);

  static aiUsageTrace = new EnhancedPath(
    `/ai-usage/:${SYSTEM_ROUTING_PARAMS.tabName}/traces/:${SYSTEM_ROUTING_PARAMS.groupKey}`,
  );

  static aiPersonas = new EnhancedPath('/ai-personas');

  static aiChatConfigurations = new EnhancedPath('/ai-chat-configurations');
  static aiChatConfiguration = new EnhancedPath(
    `${SystemPaths.aiChatConfigurations.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}/:${SYSTEM_ROUTING_PARAMS.tabName}?`,
  );

  static aiAgents = new EnhancedPath('/ai-agents');
  static aiAgentNew = new EnhancedPath(`${SystemPaths.aiAgents.path}/new`);
  static aiAgent = new EnhancedPath(
    `${SystemPaths.aiAgents.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}/:${SYSTEM_ROUTING_PARAMS.tabName}?`,
  );

  static aiSkills = new EnhancedPath('/ai-skills');
  static aiSkillNew = new EnhancedPath(`${SystemPaths.aiSkills.path}/new`);
  static aiSkill = new EnhancedPath(
    `${SystemPaths.aiSkills.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}/:${SYSTEM_ROUTING_PARAMS.tabName}?`,
  );
  static aiChatDefaults = new EnhancedPath('/ai-chat-defaults');
  static guidelinesImport = new EnhancedPath('/guidelines-import');
  static guidelines = new EnhancedPath('/system/guidelines');
  static guideline = new EnhancedPath(
    `${SystemPaths.guidelines.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}`,
  );
  static guidelineVersion = new EnhancedPath(
    `${SystemPaths.guideline.path}/versions/:${SYSTEM_ROUTING_PARAMS.versionId}`,
  );
  static guidelineSection = new EnhancedPath(
    `${SystemPaths.guidelineVersion.path}/sections/:${SYSTEM_ROUTING_PARAMS.sectionId}`,
  );
  static loanRoleRelationships = new EnhancedPath('/loan-role-relationships');

  static pipelinesBuilder = new EnhancedPath(`/pipelines-configuration`);
  static pipelines = new EnhancedPath(
    `${SystemPaths.pipelinesBuilder.path}/:${SYSTEM_ROUTING_PARAMS.pipelineId}?`,
  );

  static tags = new EnhancedPath('/tags');

  static turnTimeDefinitions = new EnhancedPath('/turn-time-metrics');

  static systemSettings = new EnhancedPath('/system-settings');

  static taskCategories = new EnhancedPath('/task-categories');

  static headerViews = new EnhancedPath('/header-views');

  static loanActionsBuilder = new EnhancedPath('/loan-actions');
  static loanActions = new EnhancedPath(
    `${SystemPaths.loanActionsBuilder.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}?`,
  );

  static loanHeaderViewBuilder = new EnhancedPath(
    `${SystemPaths.headerViews.path}/:${SYSTEM_ROUTING_PARAMS.loanHeaderViewId}`,
  );

  static commandCenterLayoutBuilder = new EnhancedPath('/command-center-layout');

  static commandCenterAnnouncements = new EnhancedPath('/command-center-announcements');

  static commandCenterClientProtection = new EnhancedPath('/command-center-client-protection');

  static commandCenterQuality = new EnhancedPath('/command-center-quality');

  static taskCategory = new EnhancedPath(
    `/task-categories/:${SYSTEM_ROUTING_PARAMS.taskCategoryId}?/:${SYSTEM_ROUTING_PARAMS.taskTypeId}?`,
  );

  static emailTemplates = new EnhancedPath(`${SystemPaths.system.path}/email-templates`);

  static cicFeeReasons = new EnhancedPath(`${SystemPaths.system.path}/cic-fee-reasons`);

  static impoundTypes = new EnhancedPath(`${SystemPaths.system.path}/impound-types`);

  static investorFeatureCodes = new EnhancedPath(
    `${SystemPaths.system.path}/investor-feature-codes`,
  );

  static programIdentifiers = new EnhancedPath(`${SystemPaths.system.path}/program-identifiers`);

  static sellerAdjustmentTypes = new EnhancedPath(
    `${SystemPaths.system.path}/seller-adjustment-types`,
  );

  static borrowerVestingTypes = new EnhancedPath(
    `${SystemPaths.system.path}/borrower-vesting-types`,
  );

  static feeProviders = new EnhancedPath(`${SystemPaths.system.path}/fee-providers`);

  static feeProvider = new EnhancedPath(
    `${SystemPaths.feeProviders.path}/:${SYSTEM_ROUTING_PARAMS.feeProviderCategoryId}?/:${SYSTEM_ROUTING_PARAMS.feeProviderId}?`,
  );

  static feeTypes = new EnhancedPath(`${SystemPaths.system.path}/fee-types`);

  static roleDetails = new EnhancedPath(
    `${SystemPaths.roles.path}/:${SYSTEM_ROUTING_PARAMS.roleId}/:${SYSTEM_ROUTING_PARAMS.tabName}`,
  );

  static documentExtractionFields = new EnhancedPath(
    `${SystemPaths.categoryManager.path}/:${SYSTEM_ROUTING_PARAMS.documentTypeId}/document-extraction-fields`,
  );

  static calculationFields = new EnhancedPath(
    `${SystemPaths.categoryManager.path}/:${SYSTEM_ROUTING_PARAMS.documentTypeId}/calculation-fields`,
  );

  static checklists = new EnhancedPath('/checklists');

  static checklistsManager = new EnhancedPath(
    `${SystemPaths.checklists.path}/:${SYSTEM_ROUTING_PARAMS.checklistEntityId}?`,
  );

  static appraisalProductBuilder = new EnhancedPath('/appraisal-product-criteria-builder');

  static appraisalProductCriteria = new EnhancedPath(
    `${SystemPaths.appraisalProductBuilder.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}?`,
  );

  static automatedActionCriteriaBuilder = new EnhancedPath('/automated-action-criteria');
  static automatedActionCriteria = new EnhancedPath(
    `${SystemPaths.automatedActionCriteriaBuilder.path}/:${SYSTEM_ROUTING_PARAMS.criteriaId}?`,
  );

  static autoLockRulesBuilder = new EnhancedPath('/auto-lock-rules');
  static autoLockRules = new EnhancedPath(
    `${SystemPaths.autoLockRulesBuilder.path}/:${SYSTEM_ROUTING_PARAMS.ruleId}?`,
  );

  static denialReasons = new EnhancedPath('/denial-reasons');

  static minDeactivationTypes = new EnhancedPath(
    `${SystemPaths.system.path}/min-deactivation-types`,
  );

  static unsalableResponsibleParties = new EnhancedPath(
    `${SystemPaths.system.path}/unsalable-responsible-parties`,
  );

  static unsalableReasons = new EnhancedPath(`${SystemPaths.system.path}/unsalable-reasons`);

  static exceptionSubCategories = new EnhancedPath('/exception-sub-categories');

  static exceptionTrackingReport = new EnhancedPath('/exception-tracking-report');

  static appraisalProducts = new EnhancedPath('/appraisal-products');

  static homeVisionJobTypes = new EnhancedPath('/homevision-job-types');

  static appraisalProductPricing = new EnhancedPath('/appraisal-product-pricing');

  static appraisalSurcharges = new EnhancedPath('/appraisal-surcharges');

  static appraisalSurchargeCriteria = new EnhancedPath('/appraisal-surcharge-criteria');

  static appKeys = new EnhancedPath('/app-keys');

  static customFieldDefinitions = new EnhancedPath('/custom-field-definitions');
  static customFieldDefinitionsTab = new EnhancedPath(
    `${SystemPaths.customFieldDefinitions.path}/:${SYSTEM_ROUTING_PARAMS.customFieldDefinitionsTab}?`,
  );

  static contactCustomFieldDefinitions = new EnhancedPath('/contact-custom-field-definitions');

  static loanEditorCustomForms = new EnhancedPath('/loan-editor-custom-forms');
  static loanEditorCustomFormDesigner = new EnhancedPath(
    `${SystemPaths.loanEditorCustomForms.path}/:${SYSTEM_ROUTING_PARAMS.loanEditorCustomFormId}`,
  );

  static propertyDefinitions = new EnhancedPath('/property-definitions');

  static webhooks = new EnhancedPath('/webhooks');

  static texmlApplications = new EnhancedPath('/texml-applications');

  static notificationDispatcher = new EnhancedPath(
    `${SystemPaths.system.path}/notification-dispatcher`,
  );

  static integrationStudioBase = new EnhancedPath('/integration-studio');
  static integrationStudio = new EnhancedPath(
    `${SystemPaths.integrationStudioBase.path}/:${SYSTEM_ROUTING_PARAMS.packageId}?`,
  );
  static integrationStudioMethod = new EnhancedPath(
    `${SystemPaths.integrationStudioBase.path}/:${SYSTEM_ROUTING_PARAMS.packageId}/methods/:${SYSTEM_ROUTING_PARAMS.methodId}`,
  );
  static integrationStudioBankrate = new EnhancedPath(
    `${SystemPaths.integrationStudioBase.path}/providers/bankrate`,
  );
  static integrationStudioShape = new EnhancedPath(
    `${SystemPaths.integrationStudioBase.path}/providers/shape`,
  );
  static integrationStudioProvider = new EnhancedPath(
    `${SystemPaths.integrationStudioBase.path}/providers/:${SYSTEM_ROUTING_PARAMS.providerKey}`,
  );
  static integrationStudioRunHistory = new EnhancedPath('/integration-studio-run-history');
}

const systemRoutesConfig = {
  settings: {
    layout: {},
  },
  routes: [
    {
      path: SystemPaths.documentTypes.path,
      component: lazyWithRetry(() => import('app/pages/documents/documentTypes')),
      pageCode: PAGE_CODES.INTERNAL_DOCUMENT_TYPE,
    },
    {
      path: SystemPaths.documentExtractionFields.path,
      component: lazyWithRetry(() => import('app/pages/documents/documentExtractionFields')),
    },
    {
      path: SystemPaths.calculationFields.path,
      component: lazyWithRetry(() => import('app/pages/system/calculationFields')),
    },
    {
      path: SystemPaths.categoryManager.path,
      component: lazyWithRetry(() => import('app/pages/system/categoryManager')),
      pageCode: PAGE_CODES.INTERNAL_DOCUMENT_CATEGORY,
    },
    {
      path: SystemPaths.contactTypesTab.path,
      component: lazyWithRetry(() => import('app/pages/system/contactTypes')),
      pageCode: PAGE_CODES.CONTACT_TYPES,
    },
    {
      path: SystemPaths.documentFieldType.path,
      component: lazyWithRetry(() => import('app/pages/documents/documentFieldTypes')),
      pageCode: PAGE_CODES.INTERNAL_DOCUMENT_FIELD_TYPE,
    },
    {
      path: SystemPaths.analytics.path,
      component: lazyWithRetry(() => import('app/pages/system/analytics')),
    },
    {
      path: SystemPaths.documentBundles.path,
      component: lazyWithRetry(() => import('app/pages/documents/documentBundles')),
      pageCode: PAGE_CODES.DOCUMENT_BUNDLE_TYPE,
    },
    {
      path: SystemPaths.documentExtractionFieldsMapping.path,
      component: lazyWithRetry(() => import('app/pages/documents/documentExtractionFieldsMapping')),
      pageCode: PAGE_CODES.DOCUMENT_EXTRACTION_FIELDS_MAPPING,
    },
    {
      path: SystemPaths.activeApplicants.path,
      component: lazyWithRetry(() => import('app/pages/system/activeApplicants')),
      pageCode: PAGE_CODES.APPLICATION,
    },
    {
      path: SystemPaths.roleDetails.path,
      component: lazyWithRetry(() => import('app/pages/system/roles/role')),
      pageCode: PAGE_CODES.ROLES,
    },
    {
      path: SystemPaths.roles.path,
      component: lazyWithRetry(() => import('app/pages/system/roles')),
      pageCode: PAGE_CODES.ROLES,
    },
    {
      path: SystemPaths.loanRoles.path,
      component: lazyWithRetry(() => import('app/pages/system/loanRoles')),
      pageCode: PAGE_CODES.LOAN_ROLE,
    },
    {
      // Builder route must precede the list route so `/drip-campaigns/:campaignId` wins.
      // Gated on crm.campaigns (DRIP_CAMPAIGNS) — activation enrolls contacts into mass
      // email/SMS/push, a CAN-SPAM/TCPA-sensitive action that must not be open to any admin.
      path: SystemPaths.dripCampaignBuilder.path,
      component: lazyWithRetry(() => import('app/pages/system/dripCampaignBuilder')),
      pageCode: PAGE_CODES.CRM_CAMPAIGNS,
    },
    {
      path: SystemPaths.dripCampaigns.path,
      component: lazyWithRetry(() => import('app/pages/system/dripCampaigns')),
      pageCode: PAGE_CODES.CRM_CAMPAIGNS,
      exact: true,
    },
    {
      path: SystemPaths.aiRuleLibraries.path,
      component: lazyWithRetry(
        () => import('app/pages/system/aiRuleLibraries/AiRuleLibrariesPage'),
      ),
      pageCode: PAGE_CODES.AI_PROCESSES,
      exact: true,
    },
    {
      path: SystemPaths.aiProcessNew.path,
      component: lazyWithRetry(() => import('app/pages/system/aiProcesses/AiProcessPage')),
      pageCode: PAGE_CODES.AI_PROCESSES,
      exact: true,
    },
    {
      path: SystemPaths.aiProcess.path,
      component: lazyWithRetry(() => import('app/pages/system/aiProcesses/AiProcessPage')),
      pageCode: PAGE_CODES.AI_PROCESSES,
    },
    {
      path: SystemPaths.aiProcesses.path,
      component: lazyWithRetry(() => import('app/pages/system/aiProcesses/AiProcessesPage')),
      pageCode: PAGE_CODES.AI_PROCESSES,
    },
    {
      path: SystemPaths.knowledgeItems.path,
      component: lazyWithRetry(() => import('app/pages/system/knowledgeItems/KnowledgeItemsPage')),
      pageCode: PAGE_CODES.KNOWLEDGE,
    },
    {
      path: SystemPaths.aiLog.path,
      component: () => <Redirect to={SystemPaths.aiUsage.generatePath()} />,
    },
    {
      path: [SystemPaths.aiUsageTrace.path, SystemPaths.aiUsage.path],
      component: lazyWithRetry(() => import('app/pages/system/aiUsage/AiUsagePage')),
      pageCode: PAGE_CODES.AI_LOG,
    },
    {
      path: SystemPaths.aiPersonas.path,
      component: lazyWithRetry(() => import('app/pages/system/aiPersonas/AiPersonasPage')),
      pageCode: PAGE_CODES.AI_PERSONAS,
    },
    {
      path: SystemPaths.aiChatConfiguration.path,
      component: lazyWithRetry(
        () => import('app/pages/system/aiChatConfigurations/AiChatConfigurationPage'),
      ),
      pageCode: PAGE_CODES.AI_CHAT_CONFIGURATIONS,
    },
    {
      path: SystemPaths.aiChatConfigurations.path,
      component: lazyWithRetry(
        () => import('app/pages/system/aiChatConfigurations/AiChatConfigurationsPage'),
      ),
      pageCode: PAGE_CODES.AI_CHAT_CONFIGURATIONS,
    },
    {
      path: SystemPaths.aiChatDefaults.path,
      component: AiChatDefaultsRedirect,
    },
    {
      path: SystemPaths.aiAgentNew.path,
      component: lazyWithRetry(() => import('app/pages/system/aiAgents/AiAgentPage')),
      pageCode: PAGE_CODES.AI_AGENTS,
      exact: true,
    },
    {
      path: SystemPaths.aiAgent.path,
      component: lazyWithRetry(() => import('app/pages/system/aiAgents/AiAgentPage')),
      pageCode: PAGE_CODES.AI_AGENTS,
    },
    {
      path: SystemPaths.aiAgents.path,
      component: lazyWithRetry(() => import('app/pages/system/aiAgents/AiAgentsPage')),
      pageCode: PAGE_CODES.AI_AGENTS,
    },
    {
      path: SystemPaths.aiSkillNew.path,
      component: lazyWithRetry(() => import('app/pages/system/aiSkills/AiSkillPage')),
      pageCode: PAGE_CODES.AI_SKILLS,
      exact: true,
    },
    {
      path: SystemPaths.aiSkill.path,
      component: lazyWithRetry(() => import('app/pages/system/aiSkills/AiSkillPage')),
      pageCode: PAGE_CODES.AI_SKILLS,
    },
    {
      path: SystemPaths.aiSkills.path,
      component: lazyWithRetry(() => import('app/pages/system/aiSkills/AiSkillsPage')),
      pageCode: PAGE_CODES.AI_SKILLS,
    },
    {
      path: SystemPaths.guidelinesImport.path,
      component: GuidelinesImportRedirect,
      pageCode: PAGE_CODES.GUIDELINES,
    },
    {
      path: SystemPaths.guidelineSection.path,
      component: GuidelineVersionRedirect,
      pageCode: PAGE_CODES.GUIDELINES,
    },
    {
      path: SystemPaths.guidelineVersion.path,
      component: GuidelineVersionRedirect,
      pageCode: PAGE_CODES.GUIDELINES,
    },
    {
      path: SystemPaths.guideline.path,
      component: lazyWithRetry(() => import('app/pages/system/guidelines/GuidelinePage')),
      pageCode: PAGE_CODES.GUIDELINES,
    },
    {
      path: SystemPaths.guidelines.path,
      component: lazyWithRetry(() => import('app/pages/system/guidelines/GuidelinesPage')),
      pageCode: PAGE_CODES.GUIDELINES,
    },
    {
      path: SystemPaths.loanRoleRelationships.path,
      component: lazyWithRetry(() => import('app/pages/system/loanRoleRelationships')),
      pageCode: PAGE_CODES.LOAN_ROLE_RELATIONSHIPS,
    },
    {
      path: SystemPaths.tags.path,
      component: lazyWithRetry(() => import('app/pages/system/tags')),
      pageCode: PAGE_CODES.TAGS,
    },
    {
      path: SystemPaths.turnTimeDefinitions.path,
      component: lazyWithRetry(() => import('app/pages/system/turnTimeDefinitions')),
      pageCode: PAGE_CODES.TAGS,
    },
    {
      path: SystemPaths.systemSettings.path,
      component: lazyWithRetry(() => import('app/pages/system/settings')),
      pageCode: PAGE_CODES.INTERNAL_SYSTEM_SETTINGS,
    },
    {
      path: SystemPaths.taskCategory.path,
      component: lazyWithRetry(() => import('app/pages/system/taskCategories')),
      pageCode: PAGE_CODES.TASK_CATEGORIES,
    },
    {
      path: SystemPaths.loanActions.path,
      component: lazyWithRetry(() => import('app/pages/system/loanActions')),
      pageCode: PAGE_CODES.LOAN_ACTIONS,
      exact: true,
    },
    {
      path: SystemPaths.headerViews.path,
      component: lazyWithRetry(() => import('app/pages/system/headerViews')),
      pageCode: ANY_HEADER_VIEW_PAGE_CODE,
      exact: true,
    },
    {
      path: SystemPaths.appraisalProducts.path,
      component: lazyWithRetry(() => import('app/pages/system/appraisalProducts')),
      pageCode: PAGE_CODES.APPRAISAL_PRODUCTS,
      exact: true,
    },
    {
      path: SystemPaths.homeVisionJobTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/homeVisionJobTypes')),
      pageCode: PAGE_CODES.HOMEVISION_JOB_TYPES,
      exact: true,
    },
    {
      path: SystemPaths.appraisalProductPricing.path,
      component: lazyWithRetry(() => import('app/pages/system/appraisalProductPricing')),
      // TODO: tighten claim once BE adds APPRAISAL_PRODUCT_PRICING claim
      pageCode: PAGE_CODES.APPRAISAL_PRODUCT_PRICING,
      exact: true,
    },
    {
      path: SystemPaths.appraisalSurcharges.path,
      component: lazyWithRetry(() => import('app/pages/system/appraisalSurcharges')),
      // TODO: tighten claim once BE adds APPRAISAL_SURCHARGES claim
      pageCode: PAGE_CODES.APPRAISAL_SURCHARGES,
      exact: true,
    },
    {
      path: SystemPaths.appraisalSurchargeCriteria.path,
      component: lazyWithRetry(() => import('app/pages/system/appraisalSurchargeCriteria')),
      pageCode: PAGE_CODES.APPRAISAL_SURCHARGE_CRITERIA,
      exact: true,
    },
    {
      path: SystemPaths.appKeys.path,
      component: lazyWithRetry(() => import('app/pages/system/appKeys')),
      pageCode: {
        pageCodes: [PAGE_CODES.COMPANY_APP_KEYS, PAGE_CODES.INTERNAL_COMPANY_APP_KEYS],
        comparisonType: PageCodeComparisonType.OR,
      },
      exact: true,
    },
    {
      path: SystemPaths.denialReasons.path,
      component: lazyWithRetry(() => import('app/pages/system/denialReasons')),
      pageCode: PAGE_CODES.DENIAL_REASONS,
      exact: true,
    },
    {
      path: SystemPaths.minDeactivationTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/minDeactivationTypes')),
      pageCode: PAGE_CODES.MIN_DEACTIVATION_TYPES,
      exact: true,
    },
    {
      path: SystemPaths.unsalableResponsibleParties.path,
      component: lazyWithRetry(() => import('app/pages/system/unsalableResponsibleParties')),
      pageCode: PAGE_CODES.UNSALABLE_RESPONSIBLE_PARTIES,
      exact: true,
    },
    {
      path: SystemPaths.unsalableReasons.path,
      component: lazyWithRetry(() => import('app/pages/system/unsalableReasons')),
      pageCode: PAGE_CODES.UNSALABLE_REASONS,
      exact: true,
    },
    {
      path: SystemPaths.exceptionSubCategories.path,
      component: lazyWithRetry(() => import('app/pages/system/exceptionSubCategories')),
      pageCode: PAGE_CODES.EXCEPTION_SUB_CATEGORIES,
      exact: true,
    },
    {
      path: SystemPaths.exceptionTrackingReport.path,
      component: lazyWithRetry(() => import('app/pages/system/loanExceptionsReport')),
      pageCode: PAGE_CODES.EXCEPTION_TRACKING_REPORT,
      exact: true,
    },
    {
      path: SystemPaths.loanHeaderViewBuilder.path,
      component: lazyWithRetry(() => import('app/pages/system/loanHeaderViewBuilder')),
      pageCode: ANY_HEADER_VIEW_PAGE_CODE,
      exact: true,
    },
    {
      path: SystemPaths.commandCenterLayoutBuilder.path,
      component: lazyWithRetry(() => import('app/pages/system/commandCenterLayoutBuilder')),
      pageCode: PAGE_CODES.HEADER_VIEW_LOAN,
      exact: true,
    },
    {
      path: SystemPaths.commandCenterAnnouncements.path,
      component: lazyWithRetry(() => import('app/pages/system/commandCenterAnnouncements')),
      pageCode: PAGE_CODES.COMMAND_CENTER_MANAGE_ANNOUNCEMENTS,
      exact: true,
    },
    {
      path: SystemPaths.commandCenterClientProtection.path,
      component: lazyWithRetry(() => import('app/pages/system/commandCenterClientProtection')),
      pageCode: PAGE_CODES.SYSTEM_PROPERTIES,
      exact: true,
    },
    {
      path: SystemPaths.commandCenterQuality.path,
      component: lazyWithRetry(() => import('app/pages/system/commandCenterQuality')),
      pageCode: PAGE_CODES.SYSTEM_PROPERTIES,
      exact: true,
    },
    {
      path: SystemPaths.emailTemplates.path,
      component: lazyWithRetry(() => import('app/pages/system/emailNotifications/templates')),
      pageCode: PAGE_CODES.EMAIL_TEMPLATE,
    },
    {
      path: SystemPaths.feeTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/feeTypes')),
      pageCode: PAGE_CODES.FEE_TYPES,
    },
    {
      path: SystemPaths.cicFeeReasons.path,
      component: lazyWithRetry(() => import('app/pages/system/cicFeeReasons')),
      pageCode: PAGE_CODES.CIC_FEE_REASONS,
    },
    {
      path: SystemPaths.impoundTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/impoundTypes')),
      pageCode: PAGE_CODES.FEE_TYPES,
    },
    {
      path: SystemPaths.investorFeatureCodes.path,
      component: lazyWithRetry(() => import('app/pages/system/investorFeatureCodes')),
      pageCode: PAGE_CODES.INVESTOR_FEATURE_CODES,
    },
    {
      path: SystemPaths.programIdentifiers.path,
      component: lazyWithRetry(() => import('app/pages/system/programIdentifiers')),
      pageCode: PAGE_CODES.PROGRAM_IDENTIFIERS,
    },
    {
      path: SystemPaths.sellerAdjustmentTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/sellerAdjustmentTypes')),
      pageCode: PAGE_CODES.SELLER_ADJUSTMENT_TYPES,
    },
    {
      path: SystemPaths.borrowerVestingTypes.path,
      component: lazyWithRetry(() => import('app/pages/system/borrowerVestingTypes')),
      pageCode: PAGE_CODES.VESTING_TYPE,
    },
    {
      path: SystemPaths.feeProvider.path,
      component: lazyWithRetry(() => import('app/pages/system/feeProviders')),
      pageCode: {
        pageCodes: [PAGE_CODES.FEE_PROVIDER_CATEGORY, PAGE_CODES.FEE_PROVIDER],
        comparisonType: PageCodeComparisonType.OR,
      },
    },
    {
      path: SystemPaths.pipelines.path,
      component: lazyWithRetry(() => import('app/pages/system/pipelines')),
      pageCode: PAGE_CODES.PIPELINES,
    },
    {
      path: SystemPaths.appraisalProductCriteria.path,
      component: lazyWithRetry(() => import('app/pages/system/appraisalProductCriteria')),
      pageCode: PAGE_CODES.APPRAISAL_PRODUCT_CRITERIA,
    },
    {
      path: SystemPaths.autoLockRules.path,
      component: lazyWithRetry(() => import('app/pages/system/autoLockRules')),
      pageCode: PAGE_CODES.AUTO_LOCK_RULES_MANAGEMENT,
    },
    {
      path: SystemPaths.automatedActionCriteria.path,
      component: lazyWithRetry(() => import('app/pages/system/automatedActionCriteria')),
      pageCode: PAGE_CODES.AUTOMATED_ACTION_CRITERIA,
    },
    {
      path: SystemPaths.checklistsManager.path,
      component: lazyWithRetry(() => import('app/pages/system/checklists')),
      pageCode: PAGE_CODES.CHECKLISTS,
    },
    {
      path: SystemPaths.customFieldDefinitionsTab.path,
      component: lazyWithRetry(
        () => import('app/pages/system/customFieldDefinitions/CustomFieldDefinitionsPage'),
      ),
      pageCode: PAGE_CODES.CUSTOM_FIELD_DEFINITIONS,
    },
    {
      path: SystemPaths.contactCustomFieldDefinitions.path,
      component: lazyWithRetry(
        () =>
          import('app/pages/system/contactCustomFieldDefinitions/ContactCustomFieldDefinitionsPage'),
      ),
      pageCode: PAGE_CODES.CRM_CONTACTS,
    },
    {
      path: SystemPaths.loanEditorCustomFormDesigner.path,
      component: lazyWithRetry(
        () => import('app/pages/system/loanEditorCustomForms/LoanEditorCustomFormDesignerPage'),
      ),
      pageCode: PAGE_CODES.LOAN_EDITOR_CUSTOM_FORMS,
      exact: true,
    },
    {
      path: SystemPaths.loanEditorCustomForms.path,
      component: lazyWithRetry(
        () => import('app/pages/system/loanEditorCustomForms/LoanEditorCustomFormsPage'),
      ),
      pageCode: PAGE_CODES.LOAN_EDITOR_CUSTOM_FORMS,
      exact: true,
    },
    {
      path: SystemPaths.propertyDefinitions.path,
      component: lazyWithRetry(() => import('app/pages/system/propertyDefinitions')),
      pageCode: PAGE_CODES.INTERNAL_COMPANIES_SYSTEM_PROPERTIES,
    },
    {
      path: SystemPaths.webhooks.path,
      component: lazyWithRetry(() => import('app/pages/system/webhooks')),
      pageCode: PAGE_CODES.WEBHOOKS,
      exact: true,
    },
    {
      path: SystemPaths.texmlApplications.path,
      component: lazyWithRetry(() => import('app/pages/system/texmlApplications')),
      pageCode: PAGE_CODES.TEXML_APPLICATIONS,
    },
    {
      path: SystemPaths.notificationDispatcher.path,
      component: lazyWithRetry(() => import('app/pages/system/notificationDispatcher')),
      pageCode: PAGE_CODES.NOTIFICATION_DISPATCHER,
      exact: true,
    },
    {
      path: SystemPaths.integrationStudioRunHistory.path,
      component: lazyWithRetry(() => import('app/pages/system/integrationStudio/RunHistoryPage')),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO_VIEW,
      exact: true,
    },
    {
      path: SystemPaths.integrationStudioMethod.path,
      component: lazyWithRetry(
        () => import('app/pages/system/integrationStudio/MethodBuilderPage'),
      ),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO,
      exact: true,
    },
    {
      // More specific than integrationStudioProvider — must precede it so `/providers/bankrate`
      // renders the bespoke lead-provider page instead of the generic ProviderSetupPage.
      path: SystemPaths.integrationStudioBankrate.path,
      component: lazyWithRetry(() => import('app/pages/system/integrationStudio/bankrate')),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO_VIEW,
      exact: true,
    },
    {
      // More specific than integrationStudioProvider — must precede it so `/providers/shape`
      // renders the bespoke lead-provider page instead of the generic ProviderSetupPage.
      path: SystemPaths.integrationStudioShape.path,
      component: lazyWithRetry(() => import('app/pages/system/integrationStudio/shape')),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO_VIEW,
      exact: true,
    },
    {
      path: SystemPaths.integrationStudioProvider.path,
      component: lazyWithRetry(
        () => import('app/pages/system/integrationStudio/ProviderSetupPage'),
      ),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO,
      exact: true,
    },
    {
      path: SystemPaths.integrationStudio.path,
      component: lazyWithRetry(
        () => import('app/pages/system/integrationStudio/IntegrationStudioPage'),
      ),
      pageCode: PAGE_CODES.INTEGRATION_STUDIO,
      exact: false,
    },
  ],
};

export default systemRoutesConfig;
